import { createSelector } from '@reduxjs/toolkit';
import pipe from 'lodash/flow';
import { isFetchingUser } from '../../../utils/redux/authentication/selectors';
import { LoginModuleState } from './loginSlice';

const rootSelector = (state: Partial<LoginModuleState>) => {
  if (!state.login) {
    throw new Error(
      'Trying to read login redux state but module is not loaded. Did you forget to add the module to the page?',
    );
  }
  return state.login;
};

export const getEmail = pipe(rootSelector, (login) => login.email);

export const getStep = pipe(rootSelector, (login) => login.step);

export const getReturnUrl = pipe(rootSelector, (login) => login.returnUrl);

export const getSentEmailLink = pipe(rootSelector, (login) => login.sentEmailLink);

const getIsLoginLoading = pipe(rootSelector, (login) => login.isLoading);

const getError = pipe(rootSelector, (login) => login.error);

const PROVIDER_MAP = {
  'google.com': 'Google',
  'facebook.com': 'Facebook',
} as const;
const isProvider = (provider: string): provider is keyof typeof PROVIDER_MAP => {
  return !!PROVIDER_MAP[provider as keyof typeof PROVIDER_MAP];
};

export const getProviders = pipe(rootSelector, (login) =>
  login.providers?.filter(isProvider).map((provider) => PROVIDER_MAP[provider]),
);

export const getIsLoading = createSelector(
  getIsLoginLoading,
  isFetchingUser,
  (isLoginLoading, isFetchingUser) => isLoginLoading || isFetchingUser,
);

export const getEmailLoginErrorCode = createSelector(getError, (error) => {
  if (!error) {
    return;
  }
  if (error.code === 'auth/user-not-found') {
    return error.code;
  }
});

export const getPasswordLoginErrorCode = createSelector(getError, (error) => {
  if (!error) {
    return;
  }
  if (error.code === 'auth/wrong-password') {
    return error.code;
  }
});

export const getEmailCheckErrorCode = createSelector(getError, (error) => {
  if (!error) {
    return;
  }
  if (error.code === 'AlreadyHasPassword' || error.code === 'AlreadyHasOtherProviders') {
    return error.code;
  }
});
